/* Leia & Batce · Build your set (LAB-173, 2026-10-22). Design: creatives/sets/build-your-set/set-page.html.
 *
 * The page (sections/lb-build-set.liquid) prints the set's products as JSON. This script runs the two-piece editor:
 * the side-by-side preview, Piece 1 / Piece 2 tabs, design → color → size (then on to piece 2 by itself), a buy button
 * that names what is missing, the sticky bar and a size finder per piece.
 *
 * The size finder uses the live finder's own rule (assets/lb-size-reco.js → window.lbSizeReco.recommendGarment) with
 * the cut's live table, and the live finder's words for a garment answer (adultLine / cutLine / altFor, garment
 * branch, copied below). It reads and writes the same localStorage profile (lb_reco_v1), so a shopper who used the
 * finder on a product page sees their numbers here. No body numbers go to analytics.
 *
 * Add to cart = ONE POST to /cart/add.js with both lines (hidden `_set` property), announced to Horizon as a
 * CartLinesUpdateEvent the same way its product form does, so the cart drawer opens and morphs from the returned
 * sections. The discount is Shopify's automatic discount; nothing here changes a price.
 * window.__lbSet keeps a log for the harness (scripts/qa-build-set.py).
 *
 * have=1 (LAB-173 phase 2, 10-22; design creatives/sets/phase2/drawer.html a7): the cart drawer's set nudge links here
 * with ?d=&c=&s=&have=1. Piece 1 is already in the cart, so it is shown as "In your cart", is never added again, and
 * the button adds piece 2 only ("Add piece 2", sticky "$41.65 each"). The cart is re-read first, and again on add, so
 * a stale link can't make a third piece: no set piece in the cart -> the normal two-piece page; one -> piece 2 only,
 * with piece 1 taken from the cart line; two or more -> "already in your cart", and the button only opens the cart.
 * 2.1 (10-23): in that "already in your cart" state both slots show the two newest set pieces from the real cart lines
 * ("In your cart", image, design · size), the picker is hidden, "View cart" is the only button, and "+N more in your
 * cart" notes any further pieces.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const REC_KEY = 'lb_reco_v1';
const log = (window.__lbSet = window.__lbSet || []);

function track(name, props) {
  log.push({ e: name, p: props || {}, t: Date.now() });
  try { if (window.posthog && typeof window.posthog.capture === 'function') window.posthog.capture(name, props || {}); } catch (e) { /* never blocks */ }
}
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const W = (u, w) => (u ? u + (u.includes('?') ? '&' : '?') + 'width=' + w : '');
function money(cents) {
  const cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  const whole = cents % 100 === 0;
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cur, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(cents / 100);
  } catch (e) { return '$' + (cents / 100).toFixed(whole ? 0 : 2); }
}
function readStore() { try { return JSON.parse(localStorage.getItem(REC_KEY) || '{}') || {}; } catch (e) { return {}; } }
function writeStore(o) { try { localStorage.setItem(REC_KEY, JSON.stringify(o)); } catch (e) { /* private mode */ } }
const reduced = () => window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;

const I = {
  check: '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="m3 8.5 3.2 3L13 4.5"/></svg>',
  alert: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5M12 16.4v.1"/></svg>',
  ruler: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="2.5" y="8" width="19" height="8" rx="1.5"/><path d="M6.5 8v3M10 8v4M13.5 8v3M17 8v4"/></svg>',
  plus: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>',
  info: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 7.5v.5"/></svg>',
};

function init(root) {
  if (!root || root.hasAttribute('data-lb-ready')) return;
  root.setAttribute('data-lb-ready', '');
  let D;
  try { D = JSON.parse(root.querySelector('[data-lb-set-data]').textContent); } catch (e) { return; }
  const designs = D.products.filter((p) => p.v && p.v.length && p.colors && p.colors.length);
  if (!designs.length) return;
  const sizes = D.sizes && D.sizes.length ? D.sizes : ['S', 'M', 'L', 'XL', '2XL'];
  const pct = Number(D.pct) || 0;
  const G = D.garment, Gs = D.garments;
  const q = (sel) => root.querySelector(sel);
  const store = readStore();
  let units = store.units || D.units || 'metric';

  // ---- state ----
  const idx = (h) => designs.findIndex((d) => d.h === h);
  const S = {
    pieces: [{ d: null, c: null, s: null }, { d: null, c: null, s: null }],
    active: 0, errors: [null, null], finder: false, res: null, status: '', busy: false, added: false,
    have: null, haveN: 0,   // have=1: 'one' (piece 1 in the cart) | 'full' (2+ in the cart) | null
  };
  const des = (i) => (S.pieces[i].d == null ? null : designs[S.pieces[i].d]);
  const colorObj = (i) => { const d = des(i); return d && (d.colors.find((c) => c.n === S.pieces[i].c) || d.colors[0]); };
  const variant = (i) => { const d = des(i), p = S.pieces[i]; return d && p.s ? d.v.find((v) => v.c === (colorObj(i) || {}).n && v.s === p.s) : null; };
  const avail = (d, color, size) => { const v = d.v.find((x) => x.c === color && x.s === size); return !!(v && v.a); };
  const done = (i) => { const v = variant(i); return !!(v && v.a); };
  const missing = (i) => (S.pieces[i].d == null ? 'design' : !done(i) ? 'size' : null);

  // ---- arrival: URL ?d=&c=&s= (piece 1, e.g. from a product page) and ?d2=&c2=&s2=; else the section's default ----
  const P = new URLSearchParams(location.search);
  function seed(i, h, c, sz) {
    const k = idx(h); if (k < 0) return false;
    const d = designs[k], col = d.colors.find((x) => x.n === c) || d.colors.find((x) => d.v.some((v) => v.c === x.n && v.a)) || d.colors[0];
    S.pieces[i] = { d: k, c: col.n, s: sz && avail(d, col.n, sz) ? sz : null };
    return true;
  }
  const fromUrl = seed(0, P.get('d'), P.get('c'), P.get('s'));
  if (!fromUrl) seed(0, (D.def && D.def.h) || designs[0].h, D.def && D.def.c, null);
  seed(1, P.get('d2'), P.get('c2'), P.get('s2'));
  if (done(0) && !done(1)) S.active = 1;
  const HAVE = P.get('have') === '1' && fromUrl;
  if (HAVE) { S.have = 'one'; S.haveN = 1; S.active = 1; }
  track('set_view', { set: D.set, from_product: fromUrl ? P.get('d') : null, from_cart: HAVE });

  // ---- have=1: what the cart really holds of this set (variant ids of the set's own products, counted by quantity) ----
  const vidx = new Map();
  designs.forEach((d, k) => d.v.forEach((v) => vidx.set(Number(v.id), { k, c: v.c, s: v.s })));
  function setPiecesOf(cart) {   // cart.items lists the newest line first; `list` = the 2 newest pieces, by quantity
    const lines = (cart.items || []).filter((it) => vidx.has(Number(it.variant_id)));
    const list = [];
    lines.forEach((it) => { const m = vidx.get(Number(it.variant_id)); for (let j = 0; j < (Number(it.quantity) || 0) && list.length < 2; j++) list.push(m); });
    return { n: lines.reduce((a, it) => a + (Number(it.quantity) || 0), 0), first: lines.length ? vidx.get(Number(lines[0].variant_id)) : null, list };
  }
  async function readSetPieces() {
    const r = await fetch(`${ROOT}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return setPiecesOf(await r.json());
  }
  function fillFromCart(list) {   // 'full': the two newest set pieces in the cart, the older one as piece 1
    if (!list || !list.length) return;
    const two = list.slice(0, 2).reverse();
    S.pieces = [0, 1].map((i) => { const m = two[i] || two[0]; return { d: m.k, c: m.c, s: m.s }; });
    S.active = -1; S.finder = false; S.res = null; S.errors = [null, null];
  }
  function applyHave(res) {
    S.haveN = res.n;
    if (res.n === 0) { S.have = null; if (!done(0)) S.active = 0; }
    else if (res.n === 1) { S.have = 'one'; S.pieces[0] = { d: res.first.k, c: res.first.c, s: res.first.s }; S.active = 1; }
    else { S.have = 'full'; fillFromCart(res.list); }
    log.push({ e: 'have', p: { n: res.n, have: S.have }, t: Date.now() });
  }
  const eachPrice = () => Math.round(((variant(0) || variant(1) || designs[0].v[0]).p) * (100 - pct) / 100);

  // ---- preview slots ----
  function renderMore() {   // 'full' with more than 2 pieces: "+N more in your cart" under the two slots
    let more = q('[data-more]');
    if (S.have === 'full' && S.haveN > 2) {
      if (!more) { more = document.createElement('p'); more.className = 'lb-set__more'; more.setAttribute('data-more', ''); q('[data-slots]').insertAdjacentElement('afterend', more); }
      more.hidden = false; more.textContent = `+${S.haveN - 2} more in your cart`;
    } else if (more) more.hidden = true;
  }
  function renderSlots() {
    renderMore();
    q('[data-slots]').innerHTML = [0, 1].map((i) => {
      const d = des(i), c = colorObj(i), p = S.pieces[i];
      const err = S.errors[i] && i === S.active;
      const inCart = S.have === 'full' || (S.have && i === 0);
      const frame = d
        ? `<div class="lb-set__frame"><img src="${esc(W(c.img, 700))}" alt="${esc(d.s + ' in ' + c.n + ', laid flat')}" width="700" height="525">${inCart ? `<span class="lb-set__incart">${I.check}In your cart</span>` : ''}</div>`
        : `<div class="lb-set__frame"><div class="lb-set__empty">${I.plus}Add piece ${i + 1}</div></div>`;
      const cap = d
        ? `<div class="n">${esc(d.s)}</div><div class="v">${esc(c.n)}${p.s ? ' · ' + esc(p.s) : ''}</div>`
        : `<div class="n">Piece ${i + 1}</div><div class="v">Not chosen yet</div>`;
      const label = `Piece ${i + 1}: ${d ? d.s + ', ' + c.n + (p.s ? ', size ' + p.s : ', no size yet') : 'not chosen yet'}.${inCart ? ' In your cart' : ' Edit'}`;
      return `<button class="lb-set__slot${err ? ' is-error' : ''}${inCart ? ' is-incart' : ''}" type="button" aria-pressed="${S.active === i}" data-slot="${i}" aria-label="${esc(label)}"${inCart ? ' aria-disabled="true"' : ''}>${frame}<div class="lb-set__cap">${cap}</div></button>`;
    }).join('');
  }

  // ---- tabs ----
  function renderTabs() {
    q('[data-tabs]').innerHTML = [0, 1].map((i) => {
      const d = des(i), p = S.pieces[i];
      const sum = d ? `${d.s}${p.s ? ' · ' + p.s : ''}` : 'Not chosen yet';
      const inCart = S.have === 'full' || (S.have && i === 0);
      return `<button class="lb-set__tab${S.errors[i] ? ' is-error' : ''}${inCart ? ' is-incart' : ''}" type="button" role="tab" id="lb-set-tab${i}" aria-controls="lb-set-editor" aria-selected="${S.active === i}" tabindex="${S.active === i ? 0 : -1}" data-tab="${i}"${inCart ? ' aria-disabled="true"' : ''}>
        <span class="k">${inCart ? 'In your cart' : 'Piece ' + (i + 1)}${done(i) ? I.check : ''}</span><span class="s${d ? '' : ' todo'}">${esc(sum)}</span></button>`;
    }).join('');
  }

  // a tile shows the color you would get by tapping it: this piece's color, else the other piece's, else the design's first available
  function tileColor(x, i) {
    const want = [S.pieces[i].c, S.pieces[1 - i].c].filter(Boolean);
    for (const w of want) { const f = x.colors.find((c) => c.n === w); if (f) return f; }
    return x.colors.find((c) => x.v.some((v) => v.c === c.n && v.a)) || x.colors[0];
  }

  // ---- editor for the active piece ----
  function renderEditor() {
    const edEl = q('[data-editor]');
    edEl.hidden = S.have === 'full';   // nothing to choose: the set is already in the cart
    if (S.have === 'full') return;
    const i = S.active, p = S.pieces[i], d = des(i), c = colorObj(i), other = S.pieces[1 - i];
    const e = S.errors[i];
    let h = `<div class="lb-set__row" data-row="design"><div class="lb-set__row-h"><span class="lab">Design${d ? ' <span class="val">· ' + esc(d.s) + '</span>' : ''}</span></div>
      <div class="lb-set__designs" role="radiogroup" aria-label="Design for piece ${i + 1}">` +
      designs.map((x, k) => {
        const cc = tileColor(x, i);
        return `<button class="lb-set__design" type="button" role="radio" aria-checked="${p.d === k}" tabindex="${p.d === k || (p.d == null && k === 0) ? 0 : -1}" data-design="${k}"><span class="ph"><img loading="lazy" src="${esc(W(cc.img, 360))}" alt="" width="360" height="270"></span><span class="t">${esc(x.s)}</span></button>`;
      }).join('') + `</div>${e === 'design' ? `<p class="lb-set__err">${I.alert}Choose a design for piece ${i + 1}.</p>` : ''}</div>`;

    if (!d) {
      h += `<p class="lb-set__hint">Pick a design to see its colors and sizes.</p>`;
    } else {
      h += `<div class="lb-set__row"><div class="lb-set__row-h"><span class="lab">Color <span class="val">· ${esc(c.n)}</span></span></div>
        <div class="lb-set__swatches" role="radiogroup" aria-label="Color for piece ${i + 1}">` +
        d.colors.map((x) => `<button class="lb-set__sw" type="button" role="radio" aria-checked="${x.n === c.n}" tabindex="${x.n === c.n ? 0 : -1}" aria-label="${esc(x.n)}" data-color="${esc(x.n)}"><span style="background:${esc(x.bg || '#ccc')}"></span></button>`).join('') +
        `</div></div>`;
      h += `<div class="lb-set__row" data-row="size"><div class="lb-set__row-h"><span class="lab">Size${p.s ? ' <span class="val">· ' + esc(p.s) + '</span>' : ''}</span><a href="#" data-open-chart>Size guide</a></div>
        <div class="lb-set__sizes" role="radiogroup" aria-label="Size for piece ${i + 1}">` +
        sizes.map((sz) => {
          const ok = avail(d, c.n, sz);
          return `<button class="lb-set__size${ok ? '' : ' is-out'}" type="button" role="radio" aria-checked="${p.s === sz}" tabindex="${p.s === sz || (!p.s && sz === sizes[0]) ? 0 : -1}" data-size="${esc(sz)}"${ok ? '' : ' aria-disabled="true"'} aria-label="${esc(sz + (ok ? '' : ', sold out'))}">${esc(sz)}</button>`;
        }).join('') +
        `</div>${e === 'size' ? `<p class="lb-set__err">${I.alert}Choose a size for piece ${i + 1}.</p>` : ''}
        <p class="lb-set__hint">${esc(D.fact || '')}${other.s && other.d != null ? ` <b>Piece ${2 - i} is ${esc(other.s)}.</b>` : ''}</p>
        <button class="lb-set__btn lb-set__btn--line lb-set__finder-btn" type="button" data-finder aria-expanded="${S.finder}">${I.ruler}Find my size</button>
        ${S.finder ? finderHTML(i) : ''}</div>`;
    }
    const ed = q('[data-editor]');
    ed.innerHTML = h;
    ed.id = 'lb-set-editor';
    ed.setAttribute('aria-labelledby', 'lb-set-tab' + i);
  }

  // ---- the size finder, scoped to the active piece (live rule, live words) ----
  const cmToIn = (cm) => Math.round(cm / 2.54 * 2) / 2;
  function adultLine(r) {
    if (r.flat == null) return r.cut + ' fit.';
    const around = units === 'imperial' ? cmToIn(2 * r.flat) + ' in' : (2 * r.flat) + ' cm', len = units === 'imperial' ? cmToIn(r.length) + ' in' : r.length + ' cm';
    return 'Chest ' + around + ' around, length ' + len + '.';
  }
  function cutLine(r) {   // garment branch of cutLine() in assets/lb-size-reco.js
    if (r.stretch && !r.capped && !r.floored) return 'Your usual size. A stretch fabric cut close to the body, so it measures less around than you do.';
    if (r.tooSmall) return 'Our largest in this cut, and likely too snug for you: it measures less around the chest than we would suggest.';
    if (r.capped) return 'Our largest in this cut.' + (r.xl2xl ? ' It wears like a regular 2XL.' : '');
    if (r.steppedDown) return 'Cut ' + (r.cut === 'Oversized' ? 'oversized' : 'boxy') + ', so ' + r.size + ' already leaves about ' + r.room + ' cm of room around you.';
    if (r.layered) return 'One up, so there is room for a layer underneath.';
    if (r.fitBumped) return 'One up: this cut sits close to the body.';
    if (r.bumped) return 'One up for the length at your height.';
    if (r.floored) return 'Our smallest in this cut.';
    if (r.cut === 'Boxy') return 'Your usual size. Cut boxy' + (r.room != null ? ': about ' + r.room + ' cm of room around you.' : ', wide and a little short.');
    if (r.cut === 'Oversized') return 'Your usual size. Cut oversized' + (r.room != null ? ': about ' + r.room + ' cm of room around you.' : ', roomy everywhere.');
    if (r.cut === 'Cropped') return 'Sits at the waist.';
    if (r.cut === 'Slim') return 'Close to the body; between sizes, take the larger.';
    if (r.cut === 'Regular') return 'True to size.';
    return 'Your tee size. Runs roomy.';
  }
  function altFor(r) {    // garment branch of altFor()
    if (r.closer) return { text: 'One down sits closer.', size: r.closer };
    if (r.steppedDown && r.roomier) return { text: r.roomier + ', your usual, is 4 cm roomier.', size: r.roomier };
    if (r.roomier) return { text: 'Prefer it roomier?', size: r.roomier };
    return null;
  }
  function finderHTML(i) {
    const st = readStore();
    const hcm = st.h_cm || '', wkg = st.w_kg || '';
    const ft = hcm ? Math.floor(hcm / 30.48) : '', inch = hcm ? Math.round(hcm / 2.54 - Math.floor(hcm / 30.48) * 12) : '', lb = wkg ? Math.round(wkg * 2.2046) : '';
    const fit = st.fit === 'relaxed' ? 'relaxed' : 'regular';
    const metric = units === 'metric';
    const fields = metric
      ? `<div><label for="lb-set-h">Height</label><div class="lb-set__field"><input id="lb-set-h" type="number" inputmode="numeric" min="120" max="220" step="any" value="${hcm}" data-in="h-cm"><span>cm</span></div></div>
         <div><label for="lb-set-w">Weight</label><div class="lb-set__field"><input id="lb-set-w" type="number" inputmode="numeric" min="30" max="200" step="any" value="${wkg}" data-in="w-kg"><span>kg</span></div></div>`
      : `<div><label for="lb-set-h">Height</label><div class="lb-set__field"><input id="lb-set-h" type="number" inputmode="numeric" min="3" max="7" step="1" value="${ft}" data-in="h-ft" aria-label="Height, feet"><span>ft</span><input type="number" inputmode="numeric" min="0" max="11" step="1" value="${inch}" data-in="h-in" aria-label="Height, inches"><span>in</span></div></div>
         <div><label for="lb-set-w">Weight</label><div class="lb-set__field"><input id="lb-set-w" type="number" inputmode="numeric" min="66" max="440" step="1" value="${lb}" data-in="w-lb"><span>lb</span></div></div>`;
    let res = '';
    if (S.res && S.res.error) res = `<p class="lb-set__err" role="alert">${I.alert}${esc(S.res.error)}</p>`;
    else if (S.res) {
      const r = S.res.r, alt = altFor(r), otherHas = des(1 - i) != null && !S.have;
      res = `<div class="res" aria-live="polite" data-finder-result data-size="${esc(r.size)}"><div class="try">Try</div><div class="big">${esc(r.size)}</div>
        <p class="line">${esc(adultLine(r))}<br>${esc(cutLine(r))}</p>
        <div class="acts"><button class="lb-set__btn lb-set__btn--ink" type="button" data-pick="${esc(r.size)}">Select ${esc(r.size)} for piece ${i + 1}</button>
        ${otherHas ? `<button class="lb-set__linkbtn" type="button" data-pick-both="${esc(r.size)}">Use ${esc(r.size)} for both pieces</button>` : ''}
        ${alt ? `<p class="alt">${esc(alt.text)} <button class="lb-set__linkbtn" type="button" data-pick="${esc(alt.size)}">Select ${esc(alt.size)}</button></p>` : ''}</div></div>`;
    }
    return `<div class="lb-set__finder" data-finder-panel><div class="two">${fields}</div>
      <button class="lb-set__linkbtn lb-set__units" type="button" data-units-toggle>${metric ? 'Use ft / lb' : 'Use cm / kg'}</button>
      <div class="fitq"><span class="flab" id="lb-set-fitq">How you like it</span><div class="lb-set__seg" role="radiogroup" aria-labelledby="lb-set-fitq">
        <button type="button" role="radio" aria-checked="${fit === 'regular'}" data-fit="regular">Standard</button><button type="button" role="radio" aria-checked="${fit === 'relaxed'}" data-fit="relaxed">Roomier</button></div></div>
      <button class="lb-set__btn lb-set__btn--line" type="button" data-show>Show my size</button>
      ${res}<p class="note">Both pieces are the same cut, so one answer fits both.</p></div>`;
  }
  function readNums() {
    const v = (n) => { const e = q(`[data-in="${n}"]`); const x = e ? parseFloat(e.value) : NaN; return isFinite(x) ? x : null; };
    let h, w;
    if (units === 'metric') { h = v('h-cm'); w = v('w-kg'); }
    else { const ft = v('h-ft'), inch = v('h-in') || 0, lb = v('w-lb'); h = ft == null ? null : Math.round((ft * 12 + inch) * 2.54); w = lb == null ? null : Math.round(lb / 2.2046); }
    return { h, w };
  }
  function showSize() {
    const { h, w } = readNums();
    if (h == null || w == null) { S.res = { error: 'Enter your height and weight.' }; return; }
    if (h < 120 || h > 220 || w < 30 || w > 200) { S.res = { error: units === 'metric' ? 'Height 120 to 220 cm and weight 30 to 200 kg, please.' : 'Height 4 to 7 ft and weight 66 to 440 lb, please.' }; return; }
    const R = window.lbSizeReco;
    if (!R || typeof R.recommendGarment !== 'function') { S.res = { error: 'The size finder is still loading. Try again in a moment.' }; return; }
    const st = readStore(), fit = st.fit === 'relaxed' ? 'relaxed' : 'regular';
    const r = R.recommendGarment(h, w, fit, null, { cut: D.cut, table: D.table || [], sizes: sizes, body: 'unisex', blank: D.blank, ref: null, stretch: false });
    S.res = { r };
    st.h_cm = h; st.w_kg = w; st.fit = fit; st.h_ts = Date.now(); st.units = units;
    const recs = st.recs || {};
    [0, 1].forEach((k) => { const d = des(k); if (d) recs[d.h] = { a: r.size, k: null, t: Date.now() }; });
    st.recs = recs; writeStore(st);
    track('set_size_reco_result', { set: D.set, piece: S.active + 1, size: r.size, fit: fit });   // no body numbers, ever
  }

  // ---- buy button + sticky ----
  function setTotal() {
    const vs = [variant(0), variant(1)];
    const base = designs[0].v[0].p;
    const prices = vs.map((v) => (v ? v.p : base));
    return { set: prices.reduce((a, p) => a + Math.round(p * (100 - pct) / 100), 0), sep: prices.reduce((a, p) => a + p, 0) };
  }
  function ctaLabel(short) {
    if (S.busy) return 'Adding…';
    if (S.have === 'full') return S.added ? (short ? 'Added' : 'Added · View cart') : 'View cart';
    if (S.have) {
      if (done(1)) return 'Add piece 2';
      const m1 = missing(1);
      return m1 === 'design' ? 'Choose piece 2' : (short ? 'Choose size' : 'Choose a size for piece 2');
    }
    if (S.added && done(0) && done(1)) return short ? 'Added' : 'Added · View cart';
    if (done(0) && done(1)) return short ? 'Add both to cart' : `Add both to cart · ${money(setTotal().set)}`;
    const i = done(0) ? 1 : 0, m = missing(i);
    if (short) return m === 'design' ? `Choose piece ${i + 1}` : 'Choose size';
    return m === 'design' ? `Choose piece ${i + 1}` : `Choose a size for piece ${i + 1}`;
  }
  function renderBuy() {
    const t = setTotal();
    q('[data-set-price]').textContent = money(t.set);
    q('[data-sep-price]').textContent = money(t.sep);
    q('[data-sticky-price]').textContent = money(t.set);
    q('[data-cta]').textContent = ctaLabel(false);
    q('[data-sticky-cta]').textContent = ctaLabel(true);
    q('[data-cta]').setAttribute('aria-busy', S.busy ? 'true' : 'false');
    q('[data-sticky-pair]').innerHTML = [0, 1].map((i) => {
      const d = des(i), c = colorObj(i);
      return d ? `<span class="th"><img src="${esc(W(c.img, 200))}" alt="" width="200" height="150"></span>` : `<span class="th empty" aria-hidden="true"></span>`;
    }).join('');
    let line;
    if (done(0) && done(1)) line = `Sizes ${S.pieces[0].s} + ${S.pieces[1].s}`;
    else { const i = done(0) ? 1 : 0; line = missing(i) === 'design' ? `Next: a design for piece ${i + 1}` : `Next: a size for piece ${i + 1}`; }
    if (S.have === 'one') line = done(1) ? `Piece 2 · size ${S.pieces[1].s}` : (missing(1) === 'design' ? 'Next: a design for piece 2' : 'Next: a size for piece 2');
    if (S.have === 'full') line = `${S.haveN} ${Gs} in your cart`;
    q('[data-sticky-line]').textContent = line;
    renderHave();
  }
  // have=1: the sticky price reads per piece ("$41.65 each"), and one quiet line says what is already in the cart
  const stickyP = q('[data-sticky] .txt .p'), stickyP0 = stickyP ? stickyP.innerHTML : '';
  let haveMsg = null;
  function renderHave() {
    if (stickyP) {
      const want = S.have ? `<span data-sticky-price>${esc(money(eachPrice()))}</span> each` : stickyP0;
      if (stickyP.innerHTML !== want) stickyP.innerHTML = want;
    }
    if (!S.have) { if (haveMsg) haveMsg.hidden = true; return; }
    if (!haveMsg) {
      haveMsg = document.createElement('p');
      haveMsg.className = 'lb-set__have';
      haveMsg.setAttribute('data-have-msg', '');
      const lede = q('.lb-set__lede');
      if (lede) lede.insertAdjacentElement('afterend', haveMsg); else q('[data-tabs]').insertAdjacentElement('beforebegin', haveMsg);
    }
    haveMsg.hidden = false;
    haveMsg.innerHTML = I.info + '<span>' + (S.have === 'full'
      ? `Your set is already in your cart: ${S.haveN} ${esc(Gs)}, ${pct}% off each.`
      : `Piece 1 is already in your cart. Choose piece 2, and both come to ${esc(money(eachPrice()))} each.`) + '</span>';
  }
  function renderReviews() {
    const chosen = [0, 1].map((i) => des(i) && des(i).h).filter(Boolean);
    const list = q('[data-rlist]'); if (!list) return;
    const items = [...list.children];
    items.forEach((li) => { const t = li.querySelector('[data-in-set]'); if (t) t.hidden = !chosen.includes(li.dataset.handle); });
    const sorted = items.slice().sort((a, b) => chosen.includes(b.dataset.handle) - chosen.includes(a.dataset.handle));
    if (sorted.some((li, k) => li !== items[k])) sorted.forEach((li) => list.appendChild(li));
  }
  function renderStatus() { q('[data-status]').textContent = S.status; }
  function render() { renderSlots(); renderTabs(); renderEditor(); renderBuy(); renderStatus(); renderReviews(); }

  // ---- actions ----
  const header = () => { const h = document.querySelector('#header-component, header-component, .header-section'); return h ? h.getBoundingClientRect().height : 0; };
  function scrollToEl(el, pad) {
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - header() - (pad || 12);
    window.scrollTo({ top: Math.max(0, top), behavior: reduced() ? 'auto' : 'smooth' });
  }
  function setActive(i, scroll) {
    S.active = i; S.finder = false; S.res = null; render();
    if (scroll) scrollToEl(q('[data-tabs]'));
  }
  function maybeAdvance() {
    if (S.active === 0 && done(0) && !done(1)) {
      S.status = 'Piece 1 is ready. Now piece 2.'; renderStatus();
      setTimeout(() => { setActive(1, true); const f = q('.lb-set__design[tabindex="0"]'); if (f) f.focus({ preventScroll: true }); }, 350);
    } else if (done(0) && done(1)) { S.status = 'Both pieces are ready.'; renderStatus(); }
  }
  function validate() {
    for (const i of (S.have ? [1] : [0, 1])) {
      const m = missing(i);
      if (m) {
        S.errors = [null, null]; S.errors[i] = m; S.active = i; S.finder = false; S.res = null; S.status = '';
        render();
        const row = q(m === 'design' ? '[data-row="design"]' : '[data-row="size"]');
        scrollToEl(row, 60);
        const first = row && row.querySelector('[role=radio]:not([aria-disabled="true"])'); if (first) first.focus({ preventScroll: true });
        track('set_missing', { set: D.set, piece: i + 1, missing: m });
        return false;
      }
    }
    return true;
  }

  function sectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((c) => { if (c instanceof HTMLElement && c.dataset.sectionId) ids.add(c.dataset.sectionId); });
    return Array.from(ids);
  }
  async function add() {
    if (S.busy) return;
    if (S.have === 'full') { openDrawer(); return; }
    if (S.added && done(0) && done(1)) { openDrawer(); return; }
    if (!validate()) return;
    const errEl = q('[data-cta-err]'); errEl.hidden = true; errEl.textContent = '';
    if (S.have) {   // re-read the cart right before adding: a stale link must not make a third piece
      S.busy = true; renderBuy();
      let res = null;
      try { res = await readSetPieces(); } catch (e) { /* below */ }
      S.busy = false;
      if (!res) { renderBuy(); errEl.textContent = 'That didn’t go through. Please try again.'; errEl.hidden = false; return; }
      applyHave(res);
      if (S.have !== 'one') { track('set_have_changed', { set: D.set, in_cart: res.n }); render(); return; }
    }
    const v0 = variant(0), v1 = variant(1);
    const tag = D.set;
    const send = S.have ? [v1] : [v0, v1];
    const items = send.map((v) => ({ id: v.id, quantity: 1, properties: { _set: tag } }));
    S.busy = true; renderBuy();
    const deferred = CartLinesUpdateEvent.createPromise();
    root.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: 1 })),
      promise: deferred.promise,
    }));
    const sections = sectionIds();
    const props = { set: D.set, products: [des(0).h, des(1).h], sizes: [S.pieces[0].s, S.pieces[1].s], colors: [colorObj(0).n, colorObj(1).n], have: !!S.have, added: send.length };
    try {
      const r = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname }),
      });
      let res = {}; try { res = await r.json(); } catch (e) { /* empty */ }
      if (!r.ok || res.status >= 400 || res.errors) {
        const msg = res.description || (typeof res.errors === 'string' ? res.errors : '') || res.message || '';
        throw Object.assign(new Error(msg || `HTTP ${r.status}`), { userMessage: msg });
      }
      const cr = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
      const cart = await cr.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: res.sections, itemCount: send.length, source: 'lb-build-set', didError: false },
      });
      if (S.have) { const r2 = setPiecesOf(cart); S.have = 'full'; S.haveN = r2.n; fillFromCart(r2.list); }
      S.busy = false; S.added = true; S.status = `Added. Both ${Gs} are in your cart.`;
      track('set_add_to_cart', props);
      render();
    } catch (err) {
      try {
        root.dispatchEvent(new CartErrorEvent({ error: err.userMessage || 'Add to cart failed', code: 'INVALID', detail: {} }));
        const cr = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
        const cart = await cr.json();
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'lb-build-set', itemCount: 0, didError: true } });
      } catch (e) { try { deferred.resolve({ detail: { didError: true } }); } catch (e2) { /* ignore */ } }
      S.busy = false; renderBuy();
      errEl.textContent = err.userMessage ? `${err.userMessage} Try another color or size.` : 'That didn’t go through. Please try again.';
      errEl.hidden = false;
      track('set_add_failed', Object.assign({ reason: String(err.userMessage || err.message || '').slice(0, 120) }, props));
    }
  }
  function openDrawer() {
    const d = document.getElementById('cart-drawer') || document.querySelector('theme-drawer');
    if (d && typeof d.open === 'function') { try { d.open(); return; } catch (e) { /* fall through */ } }
    window.location.href = `${ROOT}cart`;
  }
  function clearAdded() { if (S.added) { S.added = false; } q('[data-cta-err]').hidden = true; }

  // ---- events ----
  root.addEventListener('keydown', (ev) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(ev.key)) return;
    const el = ev.target.closest('[role=radio], [role=tab]'); if (!el) return;
    const group = [...el.parentElement.children].filter((x) => x.matches('[role=radio]:not([aria-disabled="true"]), [role=tab]'));
    if (!group.length) return;
    const k = group.indexOf(el) + (ev.key === 'ArrowLeft' || ev.key === 'ArrowUp' ? -1 : 1);
    const next = group[(k + group.length) % group.length]; ev.preventDefault();
    const sel = next.dataset.design != null ? `[data-design="${next.dataset.design}"]` : next.dataset.color ? `[data-color="${CSS.escape(next.dataset.color)}"]`
      : next.dataset.size ? `[data-size="${CSS.escape(next.dataset.size)}"]` : next.dataset.fit ? `[data-fit="${next.dataset.fit}"]` : `[data-tab="${next.dataset.tab}"]`;
    next.click(); const again = root.querySelector(sel); if (again) again.focus();
  });

  root.addEventListener('click', (ev) => {
    const t = ev.target.closest('button, a'); if (!t || !root.contains(t)) return;
    const i = S.active, p = S.pieces[i];
    if (S.have && (t.dataset.slot === '0' || t.dataset.tab === '0')) return;
    if (S.have === 'full' && (t.dataset.slot != null || t.dataset.tab != null)) return;
    if (t.dataset.slot != null) { setActive(+t.dataset.slot, true); return; }
    if (t.dataset.tab != null) { setActive(+t.dataset.tab, false); return; }
    if (t.dataset.design != null) {
      const k = +t.dataset.design, nd = designs[k], shown = tileColor(nd, i);
      S.status = (p.c && shown.n !== p.c) ? `${p.c} isn’t made in ${nd.s}. Showing ${shown.n}.` : '';
      p.d = k; p.c = shown.n;
      if (p.s && !avail(nd, p.c, p.s)) p.s = null;
      if (S.errors[i] === 'design') S.errors[i] = null;
      clearAdded(); render(); return;
    }
    if (t.dataset.color) {
      p.c = t.dataset.color; S.status = '';
      if (p.s && !avail(des(i), p.c, p.s)) { S.status = `${p.s} is sold out in ${p.c}. Choose another size.`; p.s = null; }
      clearAdded(); render(); return;
    }
    if (t.dataset.size) {
      if (t.getAttribute('aria-disabled') === 'true') { S.status = `${t.dataset.size} is sold out in ${p.c}.`; renderStatus(); return; }
      p.s = t.dataset.size; if (S.errors[i] === 'size') S.errors[i] = null; S.status = '';
      clearAdded(); render(); const again = root.querySelector(`[data-size="${CSS.escape(p.s)}"]`); if (again) again.focus({ preventScroll: true });
      maybeAdvance(); return;
    }
    if (t.hasAttribute('data-finder')) { S.finder = !S.finder; S.res = null; render(); if (S.finder) track('set_size_reco_opened', { set: D.set, piece: i + 1 }); return; }
    if (t.hasAttribute('data-units-toggle')) { const n = readNums(); units = units === 'metric' ? 'imperial' : 'metric'; const st = readStore(); st.units = units; if (n.h) st.h_cm = n.h; if (n.w) st.w_kg = n.w; writeStore(st); S.res = null; render(); return; }
    if (t.dataset.fit) { const st = readStore(); st.fit = t.dataset.fit; writeStore(st); const n = readNums(); if (S.res && n.h && n.w) showSize(); render(); return; }
    if (t.hasAttribute('data-show')) { showSize(); render(); const r = q('[data-finder-result], [data-finder-panel] .lb-set__err'); if (r) r.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' }); return; }
    if (t.dataset.pick) {
      const sz = t.dataset.pick;
      if (!avail(des(i), colorObj(i).n, sz)) { S.status = `${sz} is sold out in ${colorObj(i).n}. Choose another color, or the next size.`; renderStatus(); return; }
      p.s = sz; S.finder = false; S.res = null; S.errors[i] = null; S.status = '';
      track('set_size_reco_followed', { set: D.set, piece: i + 1, size: sz, both: false });
      clearAdded(); render(); maybeAdvance(); return;
    }
    if (t.dataset.pickBoth) {
      const sz = t.dataset.pickBoth, out = [];
      S.pieces.forEach((pc, k) => { if (pc.d == null) return; if (avail(designs[pc.d], colorObj(k).n, sz)) pc.s = sz; else out.push(k + 1); });
      S.finder = false; S.res = null; S.errors = [null, null];
      S.status = out.length ? `${sz} is sold out for piece ${out[0]} in that color.` : (done(0) && done(1) ? `Both pieces are size ${sz}.` : `Size ${sz} set.`);
      track('set_size_reco_followed', { set: D.set, piece: i + 1, size: sz, both: true });
      clearAdded(); render(); if (!done(1) && done(0) && S.active === 0) maybeAdvance(); return;
    }
    if (t.hasAttribute('data-open-chart')) { ev.preventDefault(); const det = q('[data-chart-acc]'); det.open = true; scrollToEl(det, 20); return; }
    if (t.hasAttribute('data-cta') || t.hasAttribute('data-sticky-cta')) { add(); return; }
  });
  root.addEventListener('keydown', (ev) => { if (ev.key === 'Enter' && ev.target.matches('[data-finder-panel] input')) { ev.preventDefault(); showSize(); render(); } });

  // ---- static parts: the size guide from the live table (cm) ----
  const tbl = q('[data-chart]');
  if (tbl && D.table && D.table.length) {
    const rows = D.table.filter((r) => sizes.includes(String(r[0])));
    tbl.innerHTML = `<thead><tr><th scope="col"><span class="lb-set__vh">Measure</span></th>${rows.map((r) => `<th scope="col">${esc(r[0])}</th>`).join('')}</tr></thead>
      <tbody><tr><th scope="row">Chest</th>${rows.map((r) => `<td>${r[1]}</td>`).join('')}</tr><tr><th scope="row">Length</th>${rows.map((r) => `<td>${r[2]}</td>`).join('')}</tr></tbody>`;
  }

  // sticky: phones only; hidden while the in-flow button is on screen (live sticky-v3 behavior)
  const sticky = q('[data-sticky]');
  if (sticky && 'IntersectionObserver' in window) {
    let seen = false;
    new IntersectionObserver((es) => es.forEach((e) => { seen = true; sticky.classList.toggle('is-hidden', e.isIntersecting); }), { threshold: 0.2 })
      .observe(q('[data-buybox]'));
    setTimeout(() => { if (!seen) sticky.classList.remove('is-hidden'); }, 1200);
  }
  render();
  root.setAttribute('data-lb-rendered', '');
  if (HAVE) {
    (async () => {
      for (let a = 0; a < 3; a++) {
        try { applyHave(await readSetPieces()); render(); root.setAttribute('data-lb-have-checked', S.have || 'none'); return; }
        catch (e) { await new Promise((r) => setTimeout(r, 1500 * (a + 1))); }
      }
      root.setAttribute('data-lb-have-checked', 'failed');   // add() reads the cart again before it adds anything
    })();
  }
}

function boot() { document.querySelectorAll('[data-lb-set]').forEach(init); }
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
document.addEventListener('shopify:section:load', boot);
